import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { Child } from '../interfaces/child.interface';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';
import { ParentPinDialogComponent } from '../shared/components/parent-pin-dialog/parent-pin-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ChildAuthState {
  isChildMode: boolean;
  loggedInChild: Child | null;
}

@Injectable({
  providedIn: 'root'
})
export class ChildAuthService {
  private authState = new BehaviorSubject<ChildAuthState>({
    isChildMode: false,
    loggedInChild: null
  });

  private childSubject = new BehaviorSubject<Child | null>(null);
  private isChildModeSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {}

  // Observable for components to subscribe to auth state changes
  getAuthState(): Observable<ChildAuthState> {
    return this.authState.asObservable();
  }

  get loggedInChild$(): Observable<Child | null> {
    return this.childSubject.asObservable();
  }

  get isChildMode$(): Observable<boolean> {
    return this.isChildModeSubject.asObservable();
  }

  // Toggle between parent and child mode
  async toggleChildMode() {
    const currentState = this.authState.value;
    const currentUrl = this.location.path();
    
    if (currentState.isChildMode) {
      // Switching from child mode to parent mode - verify PIN
      const dialogRef = this.dialog.open(ParentPinDialogComponent, {
        width: '300px',
        disableClose: true,
        data: { title: 'Enter Parent PIN' }
      });

      const enteredPin = await firstValueFrom(dialogRef.afterClosed());
      if (!enteredPin) {
        // User cancelled the dialog
        return;
      }

      // Verify the PIN using Firestore
      const isPinValid = await this.authService.verifyParentPin(enteredPin);
      if (!isPinValid) {
        // Reopen the dialog with an error message
        const errorDialogRef = this.dialog.open(ParentPinDialogComponent, {
          width: '300px',
          disableClose: true,
          data: { title: 'Enter Parent PIN' }
        });
        
        // Set error message
        const dialogInstance = errorDialogRef.componentInstance as ParentPinDialogComponent;
        dialogInstance.setError('Invalid PIN. Please try again.');
        
        const retryPin = await firstValueFrom(errorDialogRef.afterClosed());
        if (!retryPin) {
          return;
        }
        
        const isRetryPinValid = await this.authService.verifyParentPin(retryPin);
        if (!isRetryPinValid) {
          this.snackBar.open('Incorrect PIN', 'Close', { duration: 3000 });
          return; // Failed again, stay in child mode
        }
      }

      // PIN verified, switch to parent mode
      this.switchToParentMode();
      
      // When switching to parent mode, refresh current page
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([currentUrl]);
      });
    } else {
      // Switching from parent mode to child mode
      this.childSubject.next(null);
      this.isChildModeSubject.next(true);
      this.authState.next({
        isChildMode: true,
        loggedInChild: null
      });
      
      // When switching to child mode, always go to home
      this.router.navigate(['/']);
    }
  }

  // Log in a child
  loginChild(child: Child): void {
    console.log('CHILD AUTH SERVICE - Logging in child:', child);
    // Always set to child mode regardless of current mode
    this.childSubject.next(child);
    this.isChildModeSubject.next(true);
    this.authState.next({
      isChildMode: true,
      loggedInChild: child
    });
    console.log('CHILD AUTH SERVICE - Child mode enabled, auth state updated');
    // this.router.navigate(['/chores']);
  }

  // Log out the current child
  logoutChild(): void {
    this.childSubject.next(null);
    this.isChildModeSubject.next(false);
    this.authState.next({
      isChildMode: false,
      loggedInChild: null
    });
    this.router.navigate(['/']);
  }

  // Check if a child is logged in
  isChildLoggedIn(): boolean {
    return !!this.childSubject.value;
  }

  // Get the currently logged in child
  getLoggedInChild(): Child | null {
    return this.childSubject.value;
  }

  // Check if in child mode
  isChildMode(): boolean {
    return this.isChildModeSubject.value;
  }

  // Method to switch to parent mode
  switchToParentMode(): void {
    this.childSubject.next(null);
    this.isChildModeSubject.next(false);
    this.authState.next({
      isChildMode: false,
      loggedInChild: null
    });
    localStorage.removeItem('childAuth');
  }

  updateChildState(child: Child): void {
    this.childSubject.next(child);
    this.authState.next({
      isChildMode: true,
      loggedInChild: child
    });
  }
  
  // Logout child but stay in child mode
  logoutChildStayInChildMode(): void {
    this.childSubject.next(null);
    this.authState.next({
      isChildMode: true, // Keep child mode active
      loggedInChild: null
    });
    this.router.navigate(['/']);
  }
}